import Link from "next/link";
import SectionHeader from "@/components/SectionHeader";
import { euro } from "@/utils/preisAnalyse";

/**
 * Der stadtspezifische Teil — zuerst für Berlin.
 *
 * Die Stadtseiten liefen bisher alle über dieselbe Vorlage: dieselben
 * Kacheln, derselbe Aufbau, nur Stadtname und ein generierter Fließtext
 * getauscht. Was fehlte, waren die Angaben, die es nur für diese Stadt gibt
 * — wer vor Ort liefert, was die Anfahrt kostet, wo man abholen kann.
 *
 * Genau das steht hier, und zwar aus den Partnerdaten gerechnet. Ein Satz
 * wie "In Berlin gibt es viele Anbieter" schreibt jeder; "alle vier liefern
 * innerhalb Berlins ohne Aufpreis" kann nur jemand schreiben, der die Daten
 * hat.
 */

function Abschnitt({ id, titel, untertitel, children }) {
  return (
    <SectionHeader id={id} title={titel} subtitle={untertitel}>
      <div className="mx-auto mt-8 max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        {children}
      </div>
    </SectionHeader>
  );
}

export default function StadtInhalt({ stadt, daten, stand }) {
  if (!daten) return null;

  const {
    lokale,
    anzahlVersand,
    abPreisVersand,
    abPreisLokal,
    medianGesamt,
    anzahlPakete,
    abholung,
    ohneAnfahrt,
    bauformen,
  } = daten;

  return (
    <>
      <Abschnitt
        id="anbieter"
        titel={`Wer liefert eine Fotobox nach ${stadt}?`}
        untertitel={
          lokale.length > 0
            ? "Anbieter vor Ort oder Versand — die Wege unterscheiden sich im Preis und im Aufwand."
            : "Per Versand kommt die Box überall an — auch dorthin, wo kein Anbieter sitzt."
        }
      >
        {lokale.length > 0 ? (
          <p>
            Für {stadt} kommen{" "}
            <strong className="font-semibold text-slate-900">
              {lokale.length + anzahlVersand} Fotoboxen
            </strong>{" "}
            in Frage: {lokale.length} von Anbietern aus der Stadt und Umgebung,
            dazu {anzahlVersand} Versandboxen, die überall in Deutschland
            ankommen.
          </p>
        ) : (
          <p>
            Aus {stadt} und dem näheren Umkreis liefert derzeit kein Anbieter
            mit eigenem Aufbau. Das ist kein Hindernis:{" "}
            <strong className="font-semibold text-slate-900">
              {anzahlVersand} Versand-Fotoboxen
            </strong>{" "}
            kommen per Paket überall in Deutschland an — auch nach {stadt}. Der
            Aufbau dauert eine Viertelstunde, und die Lieferung ist bei allen
            kostenlos.
          </p>
        )}

        {lokale.length > 0 && (
          <>
            <h3 className="mt-8 text-lg font-semibold text-slate-900">
              Anbieter aus {stadt} und Umgebung
            </h3>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[460px] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                    <th scope="col" className="py-2 pr-4 font-semibold">
                      Anbieter
                    </th>
                    <th scope="col" className="py-2 pr-4 font-semibold">
                      Bauform
                    </th>
                    <th scope="col" className="py-2 pr-4 text-right font-semibold">
                      Entfernung
                    </th>
                    <th scope="col" className="py-2 text-right font-semibold">
                      ab
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lokale.map((a) => (
                    <tr
                      key={a.name}
                      className="border-b border-slate-100 last:border-0"
                    >
                      <th
                        scope="row"
                        className="py-2.5 pr-4 font-medium text-slate-900"
                      >
                        {a.name}
                      </th>
                      <td className="py-2.5 pr-4 text-slate-600">
                        {a.bauform}
                      </td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-slate-600">
                        {a.km != null ? `${a.km} km` : "—"}
                      </td>
                      <td className="py-2.5 text-right font-semibold tabular-nums text-slate-900">
                        {euro(a.abPreis)} €
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {ohneAnfahrt === lokale.length && lokale.length > 1 && (
              <p className="mt-4">
                Bei allen {lokale.length} Anbietern liegt {stadt} innerhalb des
                Liefergebiets — <strong className="font-semibold text-slate-900">
                  die Anfahrt kostet nichts extra
                </strong>
                . Das ist nicht selbstverständlich: In vielen Städten kommt eine
                Pauschale nach Entfernung dazu.
              </p>
            )}
          </>
        )}
      </Abschnitt>

      <Abschnitt
        id="preise"
        titel={`Was kostet eine Fotobox in ${stadt}?`}
        untertitel={
          lokale.length > 0
            ? "Zwei Angebote, zwei Preisklassen — und der Unterschied hat einen Grund."
            : "Was der Versand kostet — und was im Preis schon enthalten ist."
        }
      >
        <p>
          Über alle {anzahlPakete} Pakete, die für {stadt} verfügbar sind, liegt
          die Hälfte unter{" "}
          <strong className="font-semibold text-slate-900">
            {euro(medianGesamt)} €
          </strong>
          .{" "}
          {lokale.length > 0
            ? "Dahinter stehen zwei sehr unterschiedliche Angebote:"
            : "Für diese Stadt sind das die Versandboxen — Lieferung und Rückversand sind bei allen enthalten."}
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-slate-200/70">
            <h3 className="text-base font-semibold text-slate-900">
              Versandbox ab {euro(abPreisVersand)} €
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Kommt per Paket, Aufbau in einer Viertelstunde. Für die meisten
              privaten Feiern die günstigere und einfachere Wahl.
            </p>
          </div>
          {abPreisLokal > 0 && (
            <div className="rounded-2xl bg-slate-50 p-5 ring-1 ring-slate-200/70">
              <h3 className="text-base font-semibold text-slate-900">
                Vor Ort ab {euro(abPreisLokal)} €
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Der Anbieter liefert, baut auf und holt wieder ab — bequemer,
                aber teurer als der Versand.
              </p>
            </div>
          )}
        </div>
        {abPreisLokal > 0 && (
          <p className="mt-6">
            Wer in {stadt} vor allem sparen will, nimmt eine Versandbox. Wer
            eine bestimmte Bauform oder Betreuung vor Ort braucht, findet
            beides hier — in kleineren Städten gibt es das oft gar nicht.
          </p>
        )}
      </Abschnitt>

      <Abschnitt
        id="abholen"
        titel={`Abholen oder liefern lassen in ${stadt}?`}
        untertitel="Was hinter der Frage nach Standorten steckt."
      >
        <p>
          Feste Abholstationen wie bei einem Autoverleih gibt es bei Fotoboxen
          nicht — die Anbieter arbeiten von ihrem Lager aus. Für {stadt} heißt
          das konkret:
        </p>
        <ul className="mt-4 list-disc space-y-2 pl-5 marker:text-slate-400">
          {lokale.length > 0 && (
            <li>
              <strong className="font-semibold text-slate-900">
                Liefern lassen:
              </strong>{" "}
              {lokale.length}{" "}
              {lokale.length === 1 ? "Anbieter bringt" : "Anbieter bringen"} die
              Box zu eurer Location und {lokale.length === 1 ? "holt" : "holen"}{" "}
              sie wieder ab.
            </li>
          )}
          {abholung.length > 0 && (
            <li>
              <strong className="font-semibold text-slate-900">
                Selbst abholen:
              </strong>{" "}
              Bei {abholung.join(", ")} ist die Abholung in {stadt} möglich —
              günstiger, aber ihr braucht ein Auto mit Platz.
            </li>
          )}
          <li>
            <strong className="font-semibold text-slate-900">Per Paket:</strong>{" "}
            Versandboxen kommen ein bis fünf Tage vorher an eure Adresse. In der
            Stadt ist das oft der unkomplizierteste Weg — kein Termin, keine
            Übergabe.
          </li>
        </ul>
      </Abschnitt>

      {bauformen.length > 1 && (
        <Abschnitt
          id="bauformen"
          titel={`Welche Bauformen gibt es in ${stadt}?`}
          untertitel="Nicht jede Stadt hat die Auswahl."
        >
          <p>
            Bei den Anbietern vor Ort findet ihr in {stadt}{" "}
            {bauformen.join(", ")}. Was die Bauformen unterscheiden — Größe,
            Wirkung, Platzbedarf — steht im{" "}
            <Link
              href="/#bauformen"
              className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
            >
              Überblick der Bauformen
            </Link>
            . Wer eine{" "}
            <Link
              href="/fotobox-spiegel-mieten"
              className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
            >
              Spiegel-Fotobox
            </Link>{" "}
            sucht, wird hier fündig; in kleineren Städten ist das selten der
            Fall.
          </p>
        </Abschnitt>
      )}

      <Abschnitt
        id="hochzeit"
        titel={`Fotobox für die Hochzeit in ${stadt}`}
        untertitel="Der häufigste Anlass — mit eigenen Anforderungen."
      >
        <p>
          Für eine Hochzeit zählt vor allem, dass das Papier bis zum Schluss
          reicht: Rechnet mit zwei bis drei Ausdrucken pro Gast, und mit dem
          Doppelten, wenn ihr ein Fotobox-Gästebuch führt. Welche Boxen das
          schaffen, was es kostet und wann die Box im Ablauf stehen sollte,
          steht auf der{" "}
          <Link
            href="/fotobox-mieten-hochzeit"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Seite zur Hochzeits-Fotobox
          </Link>
          .
        </p>
        <p className="mt-4">
          Für {stadt} kommt eine praktische Frage dazu: Viele Locations sind
          Altbauten oder Hinterhöfe mit begrenztem Stromanschluss und engen
          Zugängen. Klärt vorher, wo die nächste Steckdose ist und ob die Box
          durch den Hof getragen werden muss — beides entscheidet, ob ihr eine
          kompakte Säule oder eine große Spiegelbox nehmt.
        </p>
      </Abschnitt>

      {/* Der einzige Link von hier zur Startseite war bis jetzt das Logo im
          Kopf — ohne Ankertext. Für "fotobox mieten" fand Google damit kein
          internes Signal, das auf die Startseite zeigt, dafür rund 14.000
          Links zwischen den Stadtseiten. Google probierte in der Folge mal
          die Startseite, mal Münster, mal Hamm. */}
      <div className="mx-auto max-w-3xl text-left text-[15px] leading-7 text-slate-700">
        <p>
          Ihr seid noch nicht auf {stadt} festgelegt? Dann führt euch der
          vollständige{" "}
          <Link
            href="/"
            className="font-semibold text-[#FF5744] underline-offset-2 hover:underline"
          >
            Vergleich zum Fotobox mieten
          </Link>{" "}
          durch alle Anbieter — mit Filtern für Gästezahl, Lieferweg und
          Druckleistung.
        </p>
      </div>

      <p className="mx-auto max-w-3xl text-left text-xs text-slate-500">
        Anbieter, Entfernungen und Preise aus dem Vergleich, Stand {stand}. Die
        Entfernung ist die Luftlinie zwischen dem Standort des Anbieters und
        dem Stadtzentrum.
      </p>
    </>
  );
}
